/** Browser-native CSS parsing keeps selectors, nesting and newer CSS syntax intact.
 * The first cascade layer wins for important declarations, including against
 * Tailwind's unlayered important utilities. Never rewrite CSS with regexes.
 */
export const BRAND_LAYER = "mogno-brand-overrides"
export const STYLE_ID = "brand-stylesheet-overrides"
export const MAX_BYTES = 256 * 1024

export function compileStylesheet(source, browser = window, priority = "override") {
  if (typeof source !== "string") throw new TypeError("A stylesheet must be text.")
  if (new TextEncoder().encode(source).length > MAX_BYTES) {
    throw new Error("The stylesheet exceeds 256 KB.")
  }
  if (priority !== "override" && priority !== "native") {
    throw new TypeError("Stylesheet priority must be override or native.")
  }
  const normalSheet = new browser.CSSStyleSheet()
  normalSheet.replaceSync(source)
  const rules = normalSheet.cssRules.length
  let declarations = 0

  if (priority === "native") {
    // Parse only for editor diagnostics. Apply the exact source so native layer
    // creation, conditionals, animations and author importance remain intact.
    const count = cssRules => {
      for (const rule of cssRules) {
        if (rule.type === 7 || rule.type === 8) continue
        if (rule.type === 1 || rule.constructor.name === "CSSNestedDeclarations") {
          declarations += rule.style.length
        }
        if (rule.cssRules) count(rule.cssRules)
      }
    }
    count(normalSheet.cssRules)
    return {css: source, declarations, rules}
  }

  const importantSheet = new browser.CSSStyleSheet()
  importantSheet.replaceSync(source)

  const kind = rule => rule.constructor.name
  const isStyle = rule => rule.type === 1 || kind(rule) === "CSSNestedDeclarations"
  const isLayer = rule => kind(rule) === "CSSLayerBlockRule"
  const isLayerOrder = rule => kind(rule) === "CSSLayerStatementRule"
  const isNamespace = rule => kind(rule) === "CSSNamespaceRule"
  const isDefinition = rule => !isStyle(rule) && !isLayerOrder(rule) && (
    rule.type === 7 || rule.type === 8 || rule.style || !rule.cssRules
  )

  // Keep source !important rules in their own, stronger sublayer. Otherwise
  // promoting `.x {color:blue}` would defeat an earlier `color:red!important`.
  const partition = (parent, keepImportant) => {
    for (let index = parent.cssRules.length - 1; index >= 0; index--) {
      const rule = parent.cssRules[index]
      // Keyframes and descriptor rules must never gain !important. They occur
      // only in the normal copy, retaining their original layer/source order.
      if (isDefinition(rule)) {
        if (keepImportant) parent.deleteRule(index)
        continue
      }
      if (isStyle(rule)) {
        for (const name of Array.from(rule.style)) {
          const wasImportant = rule.style.getPropertyPriority(name) === "important"
          if (wasImportant !== keepImportant) {
            rule.style.removeProperty(name)
          } else {
            rule.style.setProperty(name, rule.style.getPropertyValue(name), "important")
            declarations++
          }
        }
      }
      if (rule.cssRules) partition(rule, keepImportant)
    }
  }
  partition(normalSheet, false)
  partition(importantSheet, true)

  // Normal declarations have the opposite layer precedence to important
  // declarations. Compile each original layer's direct rules into a synthetic
  // sublayer, ordered from strongest to weakest: unlayered, then source layers
  // in reverse order, recursively. This preserves hover rules, named/anonymous
  // layers, layer reopening and explicitly declared layer order after promotion.
  let nextLayer = 0
  const newLayer = () => ({id: `normal-${nextLayer++}`, children: new Map()})
  const rootLayer = newLayer()
  const ruleLayers = new WeakMap()
  const layerNameParts = name => {
    const parts = [""]
    for (let index = 0; index < name.length; index++) {
      const char = name[index]
      if (char === "\\") {
        // CSSOM canonicalizes identifiers but still escapes literal dots.
        parts[parts.length - 1] += char
        if (index + 1 < name.length) parts[parts.length - 1] += name[++index]
      } else if (char === ".") {
        parts.push("")
      } else {
        parts[parts.length - 1] += char
      }
    }
    return parts
  }
  const namedLayer = (parent, name) => {
    for (const part of layerNameParts(name)) {
      if (!parent.children.has(part)) parent.children.set(part, newLayer())
      parent = parent.children.get(part)
    }
    return parent
  }
  const collectLayers = (cssRules, parent) => {
    for (const rule of cssRules) {
      if (isLayerOrder(rule)) {
        for (const name of rule.nameList) namedLayer(parent, name)
      } else if (isLayer(rule)) {
        const layer = rule.name ? namedLayer(parent, rule.name) : newLayer()
        if (!rule.name) parent.children.set(Symbol("anonymous-layer"), layer)
        ruleLayers.set(rule, layer)
        collectLayers(rule.cssRules, layer)
      } else if (!isDefinition(rule) && rule.cssRules) {
        collectLayers(rule.cssRules, parent)
      }
    }
  }
  collectLayers(normalSheet.cssRules, rootLayer)
  const normalPriority = layer => [
    layer.id,
    ...Array.from(layer.children.values()).reverse().flatMap(normalPriority),
  ]

  // Reuse the browser's serialized at-rule prelude. The small scan only finds
  // its opening block, respecting quoted/escaped braces; declarations and
  // selectors are never parsed or rewritten as text.
  const group = (rule, content) => {
    const text = rule.cssText
    let quote = null
    for (let index = 0; index < text.length; index++) {
      const char = text[index]
      if (char === "\\") { index++; continue }
      if (quote) {
        if (char === quote) quote = null
      } else if (char === "\"" || char === "'") {
        quote = char
      } else if (char === "{") {
        return `${text.slice(0, index)}{\n${content}\n}`
      }
    }
    return text
  }
  const serializeNormal = (cssRules, layer) => Array.from(cssRules, rule => {
    if (isLayerOrder(rule) || isDefinition(rule)) return ""
    if (isLayer(rule)) return serializeNormal(rule.cssRules, ruleLayers.get(rule))
    if (isStyle(rule)) return `@layer ${layer.id} {\n${rule.cssText}\n}`
    return rule.cssRules ? group(rule, serializeNormal(rule.cssRules, layer)) : ""
  }).filter(Boolean).join("\n")
  const serializeDefinitions = cssRules => Array.from(cssRules, rule => {
    if (isStyle(rule) || isNamespace(rule)) return ""
    if (isDefinition(rule) || isLayerOrder(rule)) return rule.cssText
    return rule.cssRules ? group(rule, serializeDefinitions(rule.cssRules)) : ""
  }).filter(Boolean).join("\n")

  if (rules === 0) return {css: "", declarations: 0, rules: 0}
  const important = Array.from(importantSheet.cssRules, rule => rule.cssText).join("\n")
  const normal = serializeNormal(normalSheet.cssRules, rootLayer)
  const definitions = serializeDefinitions(normalSheet.cssRules)
  // @namespace is only valid at the top of a stylesheet, before other rules.
  // Keep font/keyframe definitions outside the forced-priority layer too: they
  // obey normal definition precedence and should beat platform definitions by
  // document order when their names match.
  const namespaces = Array.from(normalSheet.cssRules)
    .filter(isNamespace).map(rule => rule.cssText).join("\n")
  const css = `${namespaces}\n@layer ${BRAND_LAYER} {
    @layer original-important, promoted-normal;
    @layer original-important { ${important} }
    @layer promoted-normal {
      @layer ${normalPriority(rootLayer).join(", ")};
      ${normal}
    }
  }\n${definitions}`
  return {css, declarations, rules}
}

export function shouldApplyStylesheet(config, browser = window) {
  if (!config || config.mode !== "custom" || !config.css) return false
  // An accidentally hidden control must never lock an admin out of recovery.
  const query = new URLSearchParams(browser.location.search)
  if (query.get("brand_css") === "off") return false
  if (/\/(?:admin|settings)\/branding\/?$/.test(browser.location.pathname)) return false
  if (query.get("tab") === "branding" &&
      /\/committee\/(?:builders\/resources\/)?admin\/?$/.test(browser.location.pathname)) return false
  if (config.scope === "all") return true
  return config.scope === "embed" && (
    browser.self !== browser.top ||
    browser.document.documentElement.classList.contains("embed-root") ||
    browser.location.pathname.startsWith("/embed/")
  )
}

export function applyStylesheet(config, browser = window) {
  const doc = browser.document
  let style = doc.getElementById(STYLE_ID)
  if (!shouldApplyStylesheet(config, browser)) {
    if (style) style.remove()
    doc.documentElement.removeAttribute("data-brand-stylesheet")
    return
  }
  const {css} = compileStylesheet(config.css, browser, config.priority)
  // Also supports app HTML that has no Phoenix root. Insert BEFORE any other
  // styles; important rules reverse normal cascade-layer order.
  if (config.priority !== "native" && !doc.getElementById("brand-stylesheet-layer")) {
    const order = doc.createElement("style")
    order.id = "brand-stylesheet-layer"
    order.textContent = `@layer ${BRAND_LAYER};`
    doc.head.prepend(order)
  }
  if (!style) {
    style = doc.createElement("style")
    style.id = STYLE_ID
    doc.head.append(style)
  }
  style.textContent = css
  doc.documentElement.dataset.brandStylesheet = config.scope
}

export function installStylesheet(browser = window) {
  // Inert JSON is emitted with HTML-safe escaping by the server. It is never
  // evaluated as JavaScript and CSS is only ever written using textContent.
  const payload = browser.document.getElementById("brand-stylesheet-config")
  if (!payload) return
  let config
  try { config = JSON.parse(payload.textContent) } catch (_) { return }
  const apply = () => {
    try { applyStylesheet(config, browser) }
    catch (error) { console.warn("[Branding] Could not apply the custom stylesheet", error.message) }
  }
  apply()
  browser.addEventListener("phx:page-loading-stop", apply)
  browser.addEventListener("pageshow", apply)
}
